import React from 'react'
import { DATA_TEST_CLASS } from 'shared-constants/build/testIds'
import { getProductQuantityFieldName } from '@public/Showroom/productQuantityUtils'
import { Card, CARD_PADDING_SIZE } from 'components/Card'
import { Flex } from 'components/Layout'
import { type CartContextInterface, type CartContextItem } from 'providers/cart'
import { useGoogleAnalytics } from 'providers/googleAnalytics'
import { findDiscount } from 'routes/utils/findDiscount'
import { SPACE } from 'Theme'
import { type CalculatePriceCheckoutQuery } from 'types/graphql-generated'
import { ProductListItem } from './ProductListItem'
import { onRemoveItemTracking, onSetItemTracking } from './utils'

interface ProductListProps {
  cartItems: CartContextItem[]
  onRemoveCartItem: CartContextInterface['removeItem']
  onSetCartItem: CartContextInterface['setItem']
  discountItems: CalculatePriceCheckoutQuery['calculatePriceCheckout']['discountItems']
}

export const ProductList: React.FC<React.PWC<ProductListProps>> = ({
  cartItems,
  onRemoveCartItem,
  onSetCartItem,
  discountItems,
}) => {
  const { executeDataToDataLayer } = useGoogleAnalytics()

  return (
    <Flex flexDirection="column" width="100%">
      {cartItems.map((cartItem, index) => {
        const {
          id: cartItemId,
          cartQuantity,
          productVariant: {
            id,
            quantity,
            optionValues,
            managedCustomFields,
            ...restProductVariant
          },
          ...rest
        } = cartItem
        const discount = findDiscount({ id, discountItems })

        return (
          <Card
            key={id}
            hasBorders
            paddingSize={CARD_PADDING_SIZE.NONE}
            data-test-class={DATA_TEST_CLASS.CART_LIST_ITEM}
            data-test-id={DATA_TEST_CLASS.CART_LIST_ITEM}
            {...(cartItems.length !== index ? { mb: SPACE.PX_10 } : {})}
          >
            <ProductListItem
              width="100%"
              id={id}
              onRemove={() => {
                onRemoveCartItem(cartItemId)
                onRemoveItemTracking({
                  executeDataToDataLayer,
                  cartItem,
                  cartQuantity,
                })
              }}
              onSetCartItem={(quantityValue) => {
                onSetCartItem({
                  productVariantId: id,
                  quantity: quantityValue,
                })
                onSetItemTracking({
                  executeDataToDataLayer,
                  cartQuantity,
                  cartItem,
                  quantityToSet: quantityValue,
                })
              }}
              cartQuantity={cartQuantity}
              quantity={quantity}
              optionValues={optionValues}
              quantityFieldName={getProductQuantityFieldName(id)}
              managedCustomFields={managedCustomFields}
              discount={discount}
              {...rest}
              {...restProductVariant}
            />
          </Card>
        )
      })}
    </Flex>
  )
}
